'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', href: '/homepage' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Download App', href: '/app-download' },
  ];

  const moreMenuItems = [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ];

  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  return (
    <header className={`w-full bg-card shadow-md fixed top-0 left-0 right-0 z-50 ${className}`}>
      <div className="w-full px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/homepage" className="flex items-center">
            <div className="flex items-center space-x-2">
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-primary"
              >
                <path
                  d="M20 4L4 12V28L20 36L36 28V12L20 4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 20L28 16M20 20L12 16M20 20V28"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-xl font-heading font-bold text-foreground">
                Signaly
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200"
              >
                {item.label}
              </Link>
            ))}
            
            <div className="relative">
              <button
                onClick={toggleMoreMenu}
                className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200 flex items-center space-x-1"
              >
                <span>More</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`transition-transform duration-200 ${isMoreMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>
              
              {isMoreMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-lg">
                  {moreMenuItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsMoreMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors duration-200"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <Link
              href="/app-download"
              className="px-6 py-2 text-sm font-heading font-semibold text-primary-foreground bg-primary rounded-md hover:scale-105 hover:shadow-cyan-glow transition-all duration-200"
            >
              Download App
            </Link>
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="px-4 py-4 space-y-2">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200"
              >
                {item.label}
              </Link>
            ))}
            
            <div className="border-t border-border pt-2 mt-2">
              {moreMenuItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            
            <div className="pt-4">
              <Link
                href="/app-download"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-6 py-3 text-center text-sm font-heading font-semibold text-primary-foreground bg-primary rounded-md hover:scale-105 hover:shadow-cyan-glow transition-all duration-200"
              >
                Download App
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;